/**
 * Serviço de API - Consumo de Tokens
 *
 * CONTEXTO:
 * Encapsula chamadas HTTP relacionadas ao rastreamento de consumo de tokens LLM.
 * Reutiliza a instância `clienteApi` definida em `servicos/clienteApi.ts`.
 *
 * RESPONSABILIDADES:
 * - Obter resumo geral de consumo
 * - Listar consumo por usuário
 * - Listar consumo por organização
 * - Obter consumo detalhado
 *
 * PADRÕES:
 * - Erros do axios são transformados em mensagens amigáveis
 */

import type { AxiosError } from 'axios';
import { clienteApi } from './clienteApi';
import type {
  ConsumoTokensUsuario,
  ConsumoTokensOrganizacao,
  ResumoConsumoTokens,
  FiltroConsumoTokens,
} from '../tipos/tiposConsumoTokens';

// ==================== HELPERS ====================

function mapearErroAxiosParaMensagem(error: AxiosError): string {
  if (error.isAxiosError && error.response) {
    const status = error.response.status;
    const backendMensagem =
      (error.response.data as { mensagem?: string; detail?: string })?.mensagem ||
      (error.response.data as { mensagem?: string; detail?: string })?.detail;

    if (status === 401) {
      return 'Não autenticado. Faça login novamente.';
    }
    if (status === 403) {
      return backendMensagem || 'Acesso negado. Apenas administradores podem acessar este recurso.';
    }
    if (status === 404) {
      return backendMensagem || 'Recurso não encontrado.';
    }
    if (status >= 500) {
      return backendMensagem || 'Erro no servidor. Tente novamente mais tarde.';
    }
    return backendMensagem || `Erro HTTP ${status}`;
  }

  if (error && error.message) {
    return error.message;
  }

  return 'Erro desconhecido de rede.';
}

// ==================== ENDPOINTS ====================

/**
 * Obtém resumo geral de consumo de tokens.
 *
 * @param filtro Parâmetros de filtro (período, top_n)
 * @returns Resumo geral de consumo
 */
export async function obterResumoConsumoTokens(
  filtro?: FiltroConsumoTokens
): Promise<ResumoConsumoTokens> {
  try {
    const params: Record<string, unknown> = {};

    if (filtro?.dias) params.dias = filtro.dias;
    if (filtro?.data_inicio) params.data_inicio = filtro.data_inicio;
    if (filtro?.data_fim) params.data_fim = filtro.data_fim;
    if (filtro?.top_n) params.top_n = filtro.top_n;

    const resposta = await clienteApi.get<ResumoConsumoTokens>('/api/admin/tokens/resumo', {
      params,
    });
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Lista consumo de tokens por usuário.
 *
 * @param filtro Parâmetros de filtro (período, limite, ordem)
 * @returns Lista de consumo por usuário
 */
export async function listarConsumoUsuarios(
  filtro?: FiltroConsumoTokens
): Promise<ConsumoTokensUsuario[]> {
  try {
    const params: Record<string, unknown> = {};

    if (filtro?.dias) params.dias = filtro.dias;
    if (filtro?.data_inicio) params.data_inicio = filtro.data_inicio;
    if (filtro?.data_fim) params.data_fim = filtro.data_fim;
    if (filtro?.limite) params.limite = filtro.limite;
    if (filtro?.ordem) params.ordem = filtro.ordem;

    const resposta = await clienteApi.get<ConsumoTokensUsuario[]>('/api/admin/tokens/usuarios', {
      params,
    });
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Obtém consumo detalhado de um usuário específico.
 *
 * @param usuarioId ID do usuário
 * @param filtro Parâmetros de filtro (período)
 * @returns Consumo do usuário
 */
export async function obterConsumoUsuario(
  usuarioId: string,
  filtro?: FiltroConsumoTokens
): Promise<ConsumoTokensUsuario> {
  try {
    const params: Record<string, unknown> = {};

    if (filtro?.dias) params.dias = filtro.dias;
    if (filtro?.data_inicio) params.data_inicio = filtro.data_inicio;
    if (filtro?.data_fim) params.data_fim = filtro.data_fim;

    const resposta = await clienteApi.get<ConsumoTokensUsuario>(
      `/api/admin/tokens/usuarios/${usuarioId}`,
      { params }
    );
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Lista consumo de tokens por organização.
 *
 * @param filtro Parâmetros de filtro (período, limite)
 * @returns Lista de consumo por organização
 */
export async function listarConsumoOrganizacoes(
  filtro?: FiltroConsumoTokens
): Promise<ConsumoTokensOrganizacao[]> {
  try {
    const params: Record<string, unknown> = {};

    if (filtro?.dias) params.dias = filtro.dias;
    if (filtro?.data_inicio) params.data_inicio = filtro.data_inicio;
    if (filtro?.data_fim) params.data_fim = filtro.data_fim;
    if (filtro?.limite) params.limite = filtro.limite;

    const resposta = await clienteApi.get<ConsumoTokensOrganizacao[]>(
      '/api/admin/tokens/organizacoes',
      { params }
    );
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Obtém consumo detalhado de uma organização específica.
 *
 * @param organizacaoId ID da organização
 * @param filtro Parâmetros de filtro (período, incluir detalhes)
 * @returns Consumo da organização
 */
export async function obterConsumoOrganizacao(
  organizacaoId: string,
  filtro?: FiltroConsumoTokens
): Promise<ConsumoTokensOrganizacao> {
  try {
    const params: Record<string, unknown> = {};

    if (filtro?.dias) params.dias = filtro.dias;
    if (filtro?.data_inicio) params.data_inicio = filtro.data_inicio;
    if (filtro?.data_fim) params.data_fim = filtro.data_fim;
    if (filtro?.incluir_detalhes_usuarios !== undefined) {
      params.incluir_detalhes_usuarios = filtro.incluir_detalhes_usuarios;
    }

    const resposta = await clienteApi.get<ConsumoTokensOrganizacao>(
      `/api/admin/tokens/organizacoes/${organizacaoId}`,
      { params }
    );
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Resposta da sincronização de organizacao_id.
 */
export interface SincronizacaoOrganizacaoResposta {
  /** Quantidade de registros de consumo atualizados */
  registros_processados: number;
  /** Quantidade de usuários cujos registros foram atualizados */
  usuarios_atualizados: number;
  /** Registros sem usuario_id (não podem ser atualizados) */
  registros_sem_usuario: number;
  /** Usuários que não pertencem a nenhuma organização */
  usuarios_sem_organizacao: number;
  /** Lista de erros encontrados durante a sincronização */
  erros: string[];
  /** Se a operação foi concluída com sucesso */
  sucesso: boolean;
  /** Mensagem descritiva do resultado */
  mensagem: string;
}

/**
 * Sincroniza o organizacao_id nos registros de consumo de tokens.
 *
 * CONTEXTO:
 * Registros de consumo antigos podem ter organizacao_id=null mesmo quando
 * o usuário pertence a uma organização. Esta função chama o endpoint que
 * busca a organização atual de cada usuário e atualiza os registros.
 *
 * @returns Resultado da sincronização
 */
export async function sincronizarOrganizacoesConsumo(): Promise<SincronizacaoOrganizacaoResposta> {
  try {
    const resposta = await clienteApi.post<SincronizacaoOrganizacaoResposta>(
      '/api/admin/tokens/sincronizar-organizacoes'
    );
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

// ===== Export do serviço como objeto =====

export const servicoConsumoTokens = {
  obterResumoConsumoTokens,
  listarConsumoUsuarios,
  obterConsumoUsuario,
  listarConsumoOrganizacoes,
  obterConsumoOrganizacao,
  sincronizarOrganizacoesConsumo,
};

export default servicoConsumoTokens;
